/**
 * @lodex-pro/web-shared/auth/error-mapper — Amplify `AuthError` → ErrorCode mapping.
 *
 * Phase 4 / Plan 04-09 — implements Discretion Resolution #5 (RESEARCH lines 1325-1350)
 * verbatim. Mobile's `packages/mobile/src/auth/error-mapper.ts` (plan 04-07, parallel
 * wave 4) is a verbatim mirror of this file — same Cognito error → ErrorCode mapping
 * table, same userMessage copy. They are kept in lockstep so a web user and a mobile
 * user see the SAME copy for the same Cognito condition.
 *
 * Discretion #5 enumerated 13 named Cognito errors plus the NetworkError synthetic.
 * This module:
 *
 *   1. Re-exports the server-side `ERROR_CATALOG` codes that overlap with client-side
 *      conditions (`MFA_CODE_INVALID`, `MFA_CODE_EXPIRED`, `MFA_SETUP_REQUIRED`).
 *   2. Adds the client-only codes from Discretion #5 (`INVALID_CREDENTIALS`,
 *      `RATE_LIMITED`, `PASSWORD_RESET_REQUIRED`, `WEAK_PASSWORD`,
 *      `USER_ALREADY_EXISTS`, `OFFLINE`, `UNKNOWN`).
 *   3. `mapAmplifyError(err: unknown)` reads `err.name` if `err` is an object with a
 *      string `name` property and looks up the mapping. Unknown errors fall through
 *      to `UNKNOWN` with the generic "Something went wrong" copy — Cognito sub-reasons
 *      are NEVER echoed to the user (T-04-09-05 mitigation; mirrors Phase 3 D-05).
 *
 * STAY-IN-SYNC CONTRACT: any change to the mapping table here MUST be applied to
 * `packages/mobile/src/auth/error-mapper.ts` in the SAME PR. The test suites assert
 * the same names so a divergence surfaces as a test failure in BOTH packages.
 *
 * Spec: master spec §31 (three-audience error model); §43 (security checklist —
 *       error sub-reason suppression); CONTEXT D-13/D-14/D-15/D-16; RESEARCH
 *       § Discretion #5 lines 1325-1350.
 */

import type { ErrorCode as ServerErrorCode } from '@lodex-pro/shared/errors/catalog';

/**
 * Union of the server-shared error codes the client maps Amplify errors onto, PLUS
 * the client-only codes from Discretion #5 that have no server counterpart.
 *
 * `OFFLINE` is the network-failure case (no fetch response at all). Mobile may map
 * this to a sync-banner; web maps it to a re-try prompt.
 *
 * `UNKNOWN` is the catch-all. The userMessage is intentionally generic. Service-side
 * logging (Pino) captures the real Cognito error name; the user just sees "Something
 * went wrong".
 */
export type ClientErrorCode =
  | Extract<
      ServerErrorCode,
      'MFA_CODE_INVALID' | 'MFA_CODE_EXPIRED' | 'MFA_SETUP_REQUIRED' | 'INTERNAL_ERROR'
    >
  | 'INVALID_CREDENTIALS'
  | 'RATE_LIMITED'
  | 'PASSWORD_RESET_REQUIRED'
  | 'WEAK_PASSWORD'
  | 'USER_ALREADY_EXISTS'
  | 'OFFLINE'
  | 'UNKNOWN';

/**
 * What `mapAmplifyError` returns. `code` lets the consumer branch on UI affordances
 * (e.g. "show a retry button"); `userMessage` is the user-safe string ready to render.
 * `retryable` is a UI hint — `true` means "the same input might succeed on retry"
 * (e.g. network error, rate limit), `false` means "fix the input and try again"
 * (e.g. wrong password). `recoveryHint` is an optional second sentence for cases
 * where a guided next step exists (e.g. "Tap 'Send new code'").
 */
export interface MappedAuthError {
  readonly code: ClientErrorCode;
  readonly userMessage: string;
  readonly retryable: boolean;
  readonly recoveryHint?: string;
}

/**
 * The canonical Cognito-error-name → MappedAuthError table per RESEARCH Discretion #5
 * lines 1331-1346 verbatim. Strings copied character-for-character from the table.
 *
 * Why a string-keyed map (not a Record<CognitoErrorName, ...>): Amplify v6's AuthError
 * names are not exported as a literal-union type by the SDK, and listing them as a
 * runtime allowlist keyed on string is the canonical pattern. Defaulting unknown
 * names to UNKNOWN means a new Cognito error name (rare but possible) collapses to
 * the generic case without a crash.
 */
const COGNITO_ERROR_MAP: ReadonlyMap<string, MappedAuthError> = new Map([
  [
    'UserNotFoundException',
    {
      code: 'INVALID_CREDENTIALS',
      userMessage: 'Email or phone not recognized.',
      retryable: false,
    },
  ],
  [
    'NotAuthorizedException',
    {
      code: 'INVALID_CREDENTIALS',
      userMessage: "Email/phone or password didn't match. Try again.",
      retryable: false,
    },
  ],
  [
    'UserNotConfirmedException',
    {
      code: 'MFA_SETUP_REQUIRED',
      userMessage: 'Confirm your email/phone to continue.',
      retryable: false,
    },
  ],
  [
    'CodeMismatchException',
    {
      code: 'MFA_CODE_INVALID',
      userMessage: "That code didn't match. Try again.",
      retryable: true,
    },
  ],
  [
    'ExpiredCodeException',
    {
      code: 'MFA_CODE_EXPIRED',
      userMessage: 'That code expired. Send a new one.',
      retryable: false,
      recoveryHint: 'Tap "Send new code".',
    },
  ],
  [
    'LimitExceededException',
    {
      code: 'RATE_LIMITED',
      userMessage: 'Too many tries. Wait a few minutes.',
      retryable: true,
    },
  ],
  [
    'TooManyRequestsException',
    {
      code: 'RATE_LIMITED',
      userMessage: 'Too many tries. Wait a few minutes.',
      retryable: true,
    },
  ],
  [
    'PasswordResetRequiredException',
    {
      code: 'PASSWORD_RESET_REQUIRED',
      userMessage: 'Reset your password to continue.',
      retryable: false,
    },
  ],
  [
    'InvalidPasswordException',
    {
      code: 'WEAK_PASSWORD',
      userMessage: 'That password is too weak. Try a longer one.',
      retryable: false,
    },
  ],
  [
    'UsernameExistsException',
    {
      code: 'USER_ALREADY_EXISTS',
      userMessage: 'That email or phone is already in use.',
      retryable: false,
    },
  ],
  [
    'MFAMethodNotFoundException',
    {
      code: 'MFA_SETUP_REQUIRED',
      userMessage: 'Set up two-factor auth to continue.',
      retryable: false,
    },
  ],
  [
    'SoftwareTokenMFANotFoundException',
    {
      code: 'MFA_SETUP_REQUIRED',
      userMessage: 'Set up two-factor auth to continue.',
      retryable: false,
    },
  ],
  [
    'AliasExistsException',
    {
      code: 'USER_ALREADY_EXISTS',
      userMessage: 'That email or phone is already in use.',
      retryable: false,
    },
  ],
  [
    'NetworkError',
    {
      code: 'OFFLINE',
      userMessage:
        'Connect to the internet to sign in. Your shift is safe — try again when you have signal.',
      retryable: true,
    },
  ],
]);

/** The catch-all fallback when no `err.name` matches the table. */
const UNKNOWN_FALLBACK: MappedAuthError = {
  code: 'UNKNOWN',
  userMessage: 'Something went wrong. Try again.',
  retryable: true,
};

/**
 * Narrow an unknown value to `{ name: string }` without `as any` (lint forbids `any`).
 */
/**
 * Quick 260827-pwd — exported. Amplify errors are NOT reliably `Error` instances,
 * so `err instanceof Error` silently misses the name and every branch falls to the
 * generic fallback. Duck-typing `.name` is the pattern this module already uses;
 * the self-service password flows now share it rather than inventing a third.
 */
export function getErrorName(err: unknown): string | null {
  if (err === null || typeof err !== 'object') return null;
  const named = err as { name?: unknown };
  if (typeof named.name !== 'string') return null;
  return named.name;
}

/**
 * Map an Amplify (or generic) error to a user-facing code + message. Sub-reasons
 * (Cognito-specific exception names, stack traces) are NEVER returned in the user
 * message — T-04-09-05 mitigation. Callers should log the original `err` separately
 * via Pino-equivalent (web) or ConsoleLogger (mobile) for debugging.
 */
export function mapAmplifyError(err: unknown): MappedAuthError {
  const name = getErrorName(err);
  if (name === null) return UNKNOWN_FALLBACK;
  const mapped = COGNITO_ERROR_MAP.get(name);
  if (mapped !== undefined) return mapped;
  return UNKNOWN_FALLBACK;
}

/**
 * The exhaustive list of Cognito error names this module knows about. Exported so
 * tests can assert that every entry from Discretion #5's table is covered without
 * having to re-list the strings inside the test file.
 */
export const KNOWN_COGNITO_ERROR_NAMES: ReadonlyArray<string> = Array.from(
  COGNITO_ERROR_MAP.keys(),
);
